/**
 * Teamio AI Dashboard — pure UI logic for the three real stat tiles.
 *
 * Everything here is framework-free and side-effect-free so it can be unit-tested
 * without Vue/Vuex/axios (see cypress/node-tests/dashboard-tiles.test.js). The
 * store actions (src/store/modules/botAdmin.js) and the Dashboard tiles
 * (src/pages/Calendar/admin/Dashboard.vue) both import from here so the request
 * shape, the number formatting, and the honest-state copy can never drift.
 *
 * BACKEND CONTRACT (teamio-bot lib/dashboardStats.js — each handler wraps the
 * helper return in createSuccessResponse(stats), whose body IS the stats object,
 * so axios `response.data` is the object directly — no extra `.data` nesting):
 *   POST /admin/agent-stats         -> { total, today }
 *   POST /admin/receptionist-stats  -> { callsTotal, callsToday, textsTotal, textsToday }
 *   POST /admin/appointments-stats  -> { total, today, trend:[...], recent:[...] }
 *
 * All three POST { siteId, employeeId } (NO from/to — the endpoint computes
 * site-local "today" itself) and are gated by assertSiteAdmin (403 if the caller
 * isn't a site admin / super admin). A 403 is NOT a red error — the tile shows a
 * friendly "Not available" state. Any other failure is a real error.
 *
 * NEVER fabricate a number. 0 is a legitimate "no activity yet" value and is shown
 * as-is with muted "no …" subtext; only loading / not-available / error replace the
 * number with a non-numeric state.
 */

/**
 * Supported date-range keys the dashboard picker can send. MUST stay in lockstep
 * with teamio-bot lib/dashboardStats.js `SUPPORTED_RANGES` — the endpoint computes
 * the site-local window for each key itself; anything else (incl. missing) falls
 * through to 'all' (no date filter; the range count equals the all-time total).
 */
export const SUPPORTED_RANGES = ['all', 'today', 'last7', 'last30', 'thismonth'];

/** The default range — 'all' shows the full all-time totals (back-compat headline). */
export const DEFAULT_RANGE = 'all';

// Human labels for the picker options + the tile sublines. The picker, the Dashboard
// tile subline, and the Appointment Stats range card all read from here so the copy
// can never drift. (Unknown keys -> "All time" via rangeLabel below.)
const RANGE_LABELS = {
  all: 'All time',
  today: 'Today',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  thismonth: 'This month',
};

/**
 * Pure key -> human label for a range. Unknown / missing keys map to "All time"
 * (mirroring the backend's unknown->'all' fallback) so the subline is always a
 * truthful period label, never blank. Used by the picker options, the tile
 * sublines, and the Appointment Stats range card.
 */
export function rangeLabel(key) {
  return RANGE_LABELS[key] || RANGE_LABELS.all;
}

/** Normalize an incoming range key to a supported one; unknown/missing -> 'all'. */
export function normalizeRange(range) {
  return SUPPORTED_RANGES.includes(range) ? range : DEFAULT_RANGE;
}

/**
 * Build the POST body shared by all three dashboard stat endpoints.
 *
 * Carries identity { siteId, employeeId } PLUS the selected `range` key (the
 * date-range picker's window). `employeeId` is ALWAYS included (null when the user
 * isn't hydrated yet) so the request shape is stable and the backend returns its own
 * 403 rather than us silently dropping it — same contract as buildSiteCallHistoryBody
 * / buildKbBody. `range` is normalized to a supported key (unknown/missing -> 'all'),
 * and the endpoint owns the site-local windowing for that key (no from/to).
 */
export function buildDashboardStatBody({ siteId, employeeId, range } = {}) {
  return { siteId, employeeId: employeeId || null, range: normalizeRange(range) };
}

/**
 * Map an axios error to the tile's access state. A 403 (site/admin gate) becomes a
 * friendly 'not-available'; anything else (or a network error) is a real 'error'.
 * Returns one of: 'not-available' | 'error'.
 */
export function statErrorToState(error) {
  const httpStatus = error && error.response ? error.response.status : null;
  return httpStatus === 403 ? 'not-available' : 'error';
}

/**
 * Whole-number display with thousands separators. Coerces to a finite integer;
 * null/undefined/NaN -> 0 (we never render a blank or a fabricated number).
 */
export function formatStatNumber(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return Math.trunc(num).toLocaleString('en-US');
}

/**
 * The "+ N texts" secondary line for the AI Receptionist tile — only shown when
 * there is at least one text. Returns null when textsTotal <= 0 (or invalid) so the
 * caller can `v-if` it off entirely. Pluralizes ("+ 1 text" / "+ 2 texts").
 */
export function formatTextsLine(textsTotal) {
  const num = Number(textsTotal);
  if (!Number.isFinite(num) || num <= 0) return null;
  const whole = Math.trunc(num);
  return `+ ${formatStatNumber(whole)} ${whole === 1 ? 'text' : 'texts'}`;
}

/**
 * Muted subtext for TODAY's count, always scoped to today so it can't contradict the
 * all-time headline. 0 -> "None today"; otherwise "<n> today". (Previously returned an
 * all-time-sounding "No calls yet" on 0, which read as a contradiction next to a non-zero
 * total — e.g. "6 calls handled" + "No calls yet".) `noneCopy` is accepted+ignored for
 * backward-compat with existing callers.
 */
export function formatTodayLine(today, noneCopy) { // eslint-disable-line no-unused-vars
  const num = Number(today);
  const whole = Number.isFinite(num) ? Math.trunc(num) : 0;
  if (whole <= 0) return 'None today';
  return `${formatStatNumber(whole)} today`;
}

// =============================================================================
// Appointment Stats PAGE — pure logic for the "booked via Teamio" trend chart and
// the recent-bookings table (src/pages/Calendar/admin/AppointmentStats.vue).
//
// Backed by POST /admin/appointments-stats -> { total, today, trend, recent } where
//   trend  = [{ date:'YYYY-MM-DD', count:N }]  (ascending, last ~30 site-local days)
//   recent = [{ name, type, provider, date, time, patientType }] (newest first)
// `online_bookings` is a BOOKING LOG — it has NO appointment outcomes (no completed /
// cancelled / no-show / conversion), so the page NEVER renders those. Everything here
// is framework-free + side-effect-free so it can be node-tested without Vue.
// =============================================================================

/**
 * The em-dash fallback used everywhere a recent-booking field is missing. A booking
 * row can have null type/provider/date/time/patientType (the backend maps blanks to
 * null), so we render an honest placeholder rather than an empty cell or a guess.
 */
export const EMPTY_CELL = '—';

/**
 * Normalize the trend array into ascending {date,count} rows with finite, non-negative
 * integer counts. Drops rows without a date and coerces junk counts to 0 so the bar
 * math below can never divide by — or scale against — a NaN. Returns [] for non-arrays.
 */
export function normalizeTrend(trend) {
  if (!Array.isArray(trend)) return [];
  return trend
    .filter((r) => r && r.date)
    .map((r) => {
      const n = Number(r.count);
      return { date: String(r.date), count: Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0 };
    });
}

/**
 * Scale a normalized trend into bar descriptors for the inline CSS chart, mirroring
 * Huddle ProductionTrends' `value / max * 100 + '%'` height approach (no chart lib).
 *
 * Each bar gets:
 *   - count       the raw integer (for tooltip / aria)
 *   - heightPct   0..100, the bar's height as a % of the tallest day. The tallest
 *                 non-zero day is always 100. A day with 0 bookings is 0 (no stub).
 *   - label       'M/D' short label derived from the YYYY-MM-DD date (UTC-safe parse —
 *                 the backend already bucketed by SITE-LOCAL day, so we must NOT let
 *                 the browser's tz shift the calendar day; we read the parts directly).
 *   - dateLabel   'Mon D' longer label for the tooltip.
 *
 * maxCount 0 (every day empty) -> every bar is 0 — the caller shows the empty state.
 */
export function buildTrendBars(trend) {
  const rows = normalizeTrend(trend);
  const maxCount = rows.reduce((m, r) => (r.count > m ? r.count : m), 0);
  return rows.map((r) => ({
    count: r.count,
    heightPct: maxCount > 0 ? Math.round((r.count / maxCount) * 100) : 0,
    label: shortDateLabel(r.date),
    dateLabel: mediumDateLabel(r.date),
  }));
}

/** True when there are no bookings to chart (no rows, or every day is 0). */
export function trendIsEmpty(trend) {
  const rows = normalizeTrend(trend);
  if (rows.length === 0) return true;
  return rows.every((r) => r.count === 0);
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Split a 'YYYY-MM-DD' string into numeric {y,m,d} WITHOUT timezone drift, or null. */
function ymdParts(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

/** 'YYYY-MM-DD' -> 'M/D' (e.g. '2026-06-30' -> '6/30'); falls back to the raw input. */
export function shortDateLabel(ymd) {
  const p = ymdParts(ymd);
  if (!p) return String(ymd || '');
  return `${p.m}/${p.d}`;
}

/** 'YYYY-MM-DD' -> 'Mon D' (e.g. '2026-06-30' -> 'Jun 30'); falls back to the raw input. */
export function mediumDateLabel(ymd) {
  const p = ymdParts(ymd);
  if (!p || p.m < 1 || p.m > 12) return String(ymd || '');
  return `${MONTHS_SHORT[p.m - 1]} ${p.d}`;
}

/** 'new' | 'returning' (case-insensitive) -> 'New' | 'Returning'; else EMPTY_CELL. */
export function formatPatientType(patientType) {
  const v = String(patientType || '').trim().toLowerCase();
  if (v === 'new') return 'New';
  if (v === 'returning') return 'Returning';
  return EMPTY_CELL;
}

/**
 * Map a raw backend recent-booking row to the exact strings the table renders. Every
 * missing field becomes EMPTY_CELL (never a blank or a fabricated value). `date` is a
 * 'YYYY-MM-DD' string reformatted to 'Mon D' (tz-safe); `time` is already a display
 * string ("9:00 AM") so it passes through; `name` already joined server-side.
 */
export function formatRecentBooking(row) {
  const r = row || {};
  const name = String(r.name || '').trim();
  return {
    name: name || EMPTY_CELL,
    type: r.type ? String(r.type) : EMPTY_CELL,
    provider: r.provider ? String(r.provider) : EMPTY_CELL,
    date: r.date ? mediumDateLabel(r.date) : EMPTY_CELL,
    time: r.time ? String(r.time) : EMPTY_CELL,
    patientType: formatPatientType(r.patientType),
  };
}

/** True when there are no recent bookings to list. */
export function recentIsEmpty(recent) {
  return !Array.isArray(recent) || recent.length === 0;
}

/**
 * Presentational-only helper for the trend chart's x-axis: decide which bar labels
 * to actually render so a 30-day chart never crowds its tick labels (especially on a
 * narrow phone, where 30 'M/D' strings overlap into mush).
 *
 * Given the number of bars and a target maximum number of visible labels, returns a
 * stride N such that every Nth bar keeps its label and the rest hide it — the LAST bar
 * is always shown so the "today" end of the trend is anchored. This changes NO data
 * and NO bar geometry; the bars themselves are untouched (see buildTrendBars). It only
 * answers "should bar i show its label?" via `shouldShowTrendLabel`.
 *
 * @param {number} barCount  how many bars are being rendered
 * @param {number} maxLabels target max number of visible labels (default 8)
 * @returns {number} stride >= 1 (1 = show every label)
 */
export function trendLabelStride(barCount, maxLabels = 8) {
  const count = Number.isFinite(barCount) ? Math.trunc(barCount) : 0;
  const max = Number.isFinite(maxLabels) && maxLabels > 0 ? Math.trunc(maxLabels) : 1;
  if (count <= max) return 1;
  return Math.ceil(count / max);
}

/**
 * Should bar `index` (0-based, of `barCount` total) render its label, given `stride`?
 * Shows every `stride`-th bar AND always the final bar (so the most-recent day is
 * labelled). Purely presentational — never affects which bars are drawn.
 */
export function shouldShowTrendLabel(index, barCount, stride) {
  const i = Number(index);
  const n = Number(barCount);
  const s = Number.isFinite(stride) && stride > 0 ? Math.trunc(stride) : 1;
  if (!Number.isFinite(i) || !Number.isFinite(n)) return true;
  if (i === n - 1) return true; // always anchor the last (most recent) day
  return i % s === 0;
}
